/**
 * RFC 9457 Problem Details, som API'et returnerer ved alle fejl.
 * Typerne matcher `BygOverblik.Contracts.Common.ProblemTypes` i backend.
 */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  traceId?: string
  correlationId?: string
  /** Felt → fejlbeskeder. Kun til stede ved valideringsfejl. */
  errors?: Record<string, string[]>
}

export const PROBLEM_TYPES = {
  validation: 'https://bygoverblik.app/problems/validation',
  notFound: 'https://bygoverblik.app/problems/not-found',
  conflict: 'https://bygoverblik.app/problems/conflict',
  forbidden: 'https://bygoverblik.app/problems/forbidden',
  unauthorized: 'https://bygoverblik.app/problems/unauthorized',
  rateLimited: 'https://bygoverblik.app/problems/rate-limited',
} as const

/**
 * Én fejltype for hele klienten. Alt fra `fetch` – netværksfejl, HTTP-fejl,
 * ugyldig JSON – ender her, så komponenter kun skal håndtere ét format.
 */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  /** Felt → fejlbeskeder, klar til React Hook Forms `setError`. */
  readonly fieldErrors: Record<string, string[]>

  constructor(status: number, problem: ProblemDetails) {
    super(ApiError.describe(problem))
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.fieldErrors = problem.errors ?? {}
  }

  get isValidationError(): boolean {
    return this.status === 400 && Object.keys(this.fieldErrors).length > 0
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Netværksfejl og 5xx kan give mening at prøve igen. Klientfejl kan ikke. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500
  }

  /** Kort besked egnet til en toast. */
  get userMessage(): string {
    if (this.status === 0) {
      return 'Kunne ikke få forbindelse til serveren. Tjek din netværksforbindelse.'
    }
    return this.message
  }

  /**
   * Beskeden brugeren skal se.
   *
   * Feltfejlene kommer først, fordi `detail` ved en valideringsfejl altid er
   * den samme sætning: "En eller flere værdier er ugyldige." Den fortæller
   * hverken hvilket felt eller hvad der skal rettes, og en toast med den tekst
   * efterlader brugeren med at gætte. Serverens egen formulering — fx
   * "Adgangskoden skal være mindst 6 tegn." — kan handles på.
   */
  private static describe(problem: ProblemDetails): string {
    const fieldMessages = Object.values(problem.errors ?? {}).flat()

    return fieldMessages.length > 0
      ? fieldMessages.join(' ')
      : (problem.detail ?? problem.title ?? 'Der opstod en uventet fejl.')
  }

  static network(cause: unknown): ApiError {
    return new ApiError(0, {
      title: 'Ingen forbindelse',
      detail: 'Kunne ikke få forbindelse til serveren. Tjek din netværksforbindelse.',
      instance: cause instanceof Error ? cause.message : undefined,
    })
  }
}
